import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { authedApi } from "../lib/api";

const ICON_BY_TYPE = {
  story_submitted: "📥",
  story_message: "💬",
  story_status: "✅",
  mention: "@",
  job_application: "📄",
  job_status: "🎉",
  job_message: "💬",
  support_reply: "💬",
};

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "jetzt";
  if (diff < 3600_000) return `${Math.floor(diff / 60000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
};

const NotificationBell = () => {
  const { userToken, adminToken } = useAuth();
  const token = userToken || adminToken;
  const a = useMemo(() => (token ? authedApi(token) : null), [token]);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!a) return;
    setLoading(true);
    try { setItems(await a.listNotifications()); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!token) return;
    load();
    const i = setInterval(load, 30_000);
    return () => clearInterval(i);
    
  }, [token]);

  if (!token) return null;

  const unread = items.filter((n) => !n.read).length;

  const onMarkAll = async () => {
    try { await a.markAllRead(); setItems((p) => p.map((x) => ({ ...x, read: true }))); }
    catch { /* ignore */ }
  };

  const onClick = async (n) => {
    if (!n.read) {
      try { await a.markNotificationRead(n.id); }
      catch { /* ignore */ }
      setItems((p) => p.map((x) => x.id === n.id ? { ...x, read: true } : x));
    }
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell"
        aria-label="Benachrichtigungen"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-zinc-300 hover:border-white/25 hover:text-white"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notification-bell-count" className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-dropdown" className="absolute right-0 top-11 z-50 w-[340px] max-h-[420px] overflow-hidden rounded-xl border border-white/10 bg-[#0f141b] shadow-2xl shadow-black/50">
          <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
            <div className="text-xs font-semibold text-white">Benachrichtigungen</div>
            {unread > 0 && (
              <button onClick={onMarkAll} data-testid="notification-mark-all" className="inline-flex items-center gap-1 text-[10px] text-zinc-400 hover:text-white">
                <CheckCheck className="h-3 w-3" /> Alle gelesen
              </button>
            )}
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-4 w-4 animate-spin text-zinc-500" /></div>
            ) : items.length === 0 ? (
              <div className="px-3 py-10 text-center text-xs text-zinc-500">Noch keine Benachrichtigungen.</div>
            ) : items.map((n) => (
              <Link
                key={n.id}
                to={n.link || "#"}
                onClick={() => onClick(n)}
                data-testid={`notification-${n.id}`}
                className={`flex gap-3 border-b border-white/5 px-3 py-2 hover:bg-white/5 ${n.read ? "opacity-60" : ""}`}
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#7c4dff]/15 text-sm">
                  {ICON_BY_TYPE[n.type] || "🔔"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="truncate text-xs font-semibold text-white">{n.title}</div>
                    <div className="text-[10px] text-zinc-500">{formatTime(n.created_at)}</div>
                  </div>
                  {n.body && <div className="mt-0.5 line-clamp-2 text-[11px] text-zinc-400">{n.body}</div>}
                </div>
                {!n.read && <span className="self-center inline-block h-2 w-2 rounded-full bg-rose-500" />}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
